// @flow

import React from 'react';
import type { Node } from 'react';
import styled, { css } from 'styled-components';

import { useImages } from 'hooks';
import { breakLine, flex, p12, topMargin, bottomMargin, Width, widths } from 'styles';
import { ImageLoader } from 'components/Gui';
import type { AllImages } from 'hooks/use-images';

const Wrapper = styled.section`
  ${flex()};
  flex-direction: column;
  ${topMargin(0, 30, 80)};

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    flex-direction: row;
    justify-content: ${({ justifyContent }) => (justifyContent ? justifyContent : 'flex-end')};
    align-items: flex-start;
  }
`;

const Text = styled.div`
  max-width: 380px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    max-width: ${({ tabletTextWidth }) => `${tabletTextWidth}px`};
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    max-width: unset;
    width: ${({ desktopWidth }) => `${desktopWidth}px`};
    margin-left: ${({ desktopMargin }) => (desktopMargin ? `${desktopMargin}px` : 'auto')};
  }

  * {
    text-align: center;

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      text-align: left;
    }
  }

  h2 {
    margin-bottom: 24px;

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      margin-top: 20px;
    }
  }

  h3 {
    margin-bottom: 16px;
    font-size: 2rem;
    ${breakLine(true, false, false)}
  }

  p {
    margin-bottom: 24px;
  }
`;

const Buttons = styled.div`
  margin-top: 54px;
  ${flex()};

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 40px;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 42px;
    justify-content: flex-start;
  }
`;

const Disclaimer = styled.div`
  ${p12};
  margin-top: 12px;
  text-align: center;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 14px;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 11px;
    text-align: left;
  }
`;

const Image = styled.div`
  ${bottomMargin(30, 30, 0)};

  ${({ imageWidths }) =>
    css`
      ${widths(...imageWidths)}
    `};
  margin-top: 48px;
  max-width: 420px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    max-width: unset;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 0;
  }
`;

type Props = {
  title: string | Node,
  tabletTextWidth?: number,
  desktopText?: {
    width: number,
    marginLeft?: number,
  },
  imageWidths?: number[],
  image: {
    name: string,
    alt: string,
  },
  text: Node,
  buttons: Node[],
  disclaimer: string | Node,
  images: AllImages,
  justifyContent: string,
};

const LeftImageWithText = ({
  title,
  tabletTextWidth = 480,
  desktopText: { width, marginLeft } = { width: 400 },
  imageWidths = [320, 700, 900],
  image: { name, alt },
  text,
  buttons,
  disclaimer,
  images,
  justifyContent,
  ...rest
}: Props) => {
  const image = useImages(images)(name);
  return (
    <Wrapper justifyContent={justifyContent} {...rest}>
      <Image imageWidths={imageWidths}>
        <ImageLoader image={image} alt={alt} />
      </Image>
      <Text desktopWidth={width} desktopMargin={marginLeft} tabletTextWidth={tabletTextWidth}>
        {title}
        {text}
        {buttons && (
          <>
            <Buttons>{buttons}</Buttons>
            <Disclaimer>{disclaimer}</Disclaimer>
          </>
        )}
      </Text>
    </Wrapper>
  );
};

export default LeftImageWithText;
